import React from 'react';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import './StatsRow.scss';

export interface StatsRowProps {
  /** Total headcount scheduled for the active half (assigned + unassigned). */
  scheduled: number;
  /** Number of associates currently assigned to a workstation or function. */
  assigned: number;
  /** Number of scheduled associates not yet assigned anywhere. */
  unassigned: number;
  /** Number of manual override actions applied this session. */
  overrides: number;
}

/** Meridian semantic text colours the redesign mock maps onto each stat. */
type StatColor = 'success' | 'error' | undefined;

interface StatItem {
  key: string;
  label: string;
  value: number;
  color: StatColor;
}

/**
 * Top-of-page summary strip for the redesigned Station Assignment screen.
 *
 * Pure presentational: renders four headline counts — Scheduled, Assigned,
 * Unassigned and Overrides — derived and supplied by the parent page. The
 * parent recomputes these whenever the active half or an assignment changes,
 * so this component never reaches into the response itself.
 *
 * Built entirely from Meridian primitives (Box/Row/Column/Text); emphasis
 * colour comes from Text's semantic `color` prop (success/error) rather than
 * hand-rolled CSS colours.
 */
const StatsRow: React.FC<StatsRowProps> = ({
  scheduled,
  assigned,
  unassigned,
  overrides,
}) => {
  const items: StatItem[] = [
    { key: 'scheduled', label: 'Scheduled', value: scheduled, color: undefined },
    { key: 'assigned', label: 'Assigned', value: assigned, color: 'success' },
    { key: 'unassigned', label: 'Unassigned', value: unassigned, color: 'error' },
    { key: 'overrides', label: 'Overrides', value: overrides, color: undefined },
  ];

  return (
    <Box className="assignments-stats-row" type="outline" spacingInset="300" data-testid="stats-row">
      <Row spacing="600" alignmentVertical="top">
        {items.map((item) => (
          <Column
            key={item.key}
            spacing="100"
            alignmentHorizontal="center"
            data-testid={`stats-row-${item.key}`}
          >
            <Text type="h500" tag="span" alignment="center" color={item.color}>
              {item.value}
            </Text>
            <Text
              type="b100"
              tag="span"
              alignment="center"
              color="secondary"
              className="assignments-stats-row__label"
            >
              {item.label}
            </Text>
          </Column>
        ))}
      </Row>
    </Box>
  );
};

export default StatsRow;

import './StatsRow.scss';